import * as React from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useMe } from "../../hooks/useMe.js";
import { usePlacementDone } from "../../hooks/usePlacementDone.js";
import { useStartDiagnostic } from "../../hooks/useStartDiagnostic.js";
import { useSignOut } from "../../hooks/useSignOut.js";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  signedIn: boolean;
}

const PRIMARY: ReadonlyArray<{ to: string; label: string }> = [
  { to: "/games", label: "Games" },
  { to: "/quizzes", label: "Quiz Studio" },
  { to: "/homework", label: "Homework" },
  { to: "/community", label: "Community quizzes" },
  { to: "/blog", label: "Blog" },
  { to: "/diagnostic", label: "Take the placement test" },
  { to: "/plans", label: "Plans" },
];

const SECONDARY: ReadonlyArray<{ to: string; label: string }> = [
  { to: "/about", label: "About" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
];

/**
 * Off-canvas navigation drawer for the mobile top app bar (marketing + auth
 * surfaces). Portaled to <body>; closes on scrim tap or Escape. Field Day
 * styled. Auth-aware foot via useMe()-derived `signedIn`.
 */
export function Drawer({ open, onClose, signedIn }: DrawerProps): React.ReactElement | null {
  const signOut = useSignOut();
  const { data: me } = useMe();
  const { done: placementDone, inProgress: placementInProgress } = usePlacementDone();
  const { start: startDiagnostic } = useStartDiagnostic();
  const isStaff = !!me?.user?.staff_role;
  // Once a user has placed, drop the "Take the placement test" link — Games and
  // Plans already cover where they want to go, and re-offering the test is the
  // reported source of "it keeps asking me to retake it" confusion.
  const primary = placementDone ? PRIMARY.filter((l) => l.to !== "/diagnostic") : PRIMARY;

  // Keep the latest onClose without making it an effect dependency — an inline
  // arrow from the parent would re-run this effect on every parent render and
  // momentarily unlock body scroll while the drawer is open.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="vfm-drawer" role="dialog" aria-modal="true" aria-label="Menu">
      <button
        type="button"
        className="vfm-drawer__scrim"
        aria-label="Close menu"
        onClick={onClose}
      />
      <div className="vfm-drawer__panel v-field">
        <button
          type="button"
          className="vfm-drawer__close"
          aria-label="Close menu"
          onClick={onClose}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
        <nav className="vfm-drawer__nav" aria-label="Primary">
          {primary.map((l) =>
            l.to === "/diagnostic" ? (
              <button
                key={l.to}
                type="button"
                className="vfm-drawer__link"
                onClick={() => {
                  onClose();
                  void startDiagnostic();
                }}
              >
                {placementInProgress ? "Resume placement" : l.label}
              </button>
            ) : (
              <Link key={l.to} to={l.to} className="vfm-drawer__link" onClick={onClose}>
                {l.label}
              </Link>
            ),
          )}
        </nav>
        <div className="vfm-drawer__divider" />
        <nav className="vfm-drawer__nav vfm-drawer__nav--sec" aria-label="More">
          {SECONDARY.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="vfm-drawer__link vfm-drawer__link--sec"
              onClick={onClose}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="vfm-drawer__foot">
          {signedIn ? (
            <>
              <Link to="/dashboard" className="vfm-drawer__cta" onClick={onClose}>
                Dashboard
              </Link>
              {isStaff && (
                <Link
                  to="/admin"
                  className="vfm-drawer__link vfm-drawer__link--sec"
                  onClick={onClose}
                >
                  Admin
                </Link>
              )}
              <button
                type="button"
                className="vfm-drawer__signout"
                onClick={() => {
                  onClose();
                  signOut();
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/signin" className="vfm-drawer__cta" onClick={onClose}>
              Sign in
            </Link>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
